import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppSettings } from '@/features/config/hooks/use-config';
import { unwrap } from '@/lib/result';
import { useSessionStore } from '@/stores/session.store';

import { notificationsRepository } from '../api/notifications.repository';
import { requestPushToken } from '../lib/push';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
};

export function useMyNotifications() {
  return useQuery({
    queryKey: notificationKeys.list(),
    queryFn: () => unwrap(notificationsRepository.listMine()),
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(notificationsRepository.markRead(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
    },
  });
}

/**
 * Requests permission + an FCM token and registers it. Resolves to true when a
 * token was stored, false when push is unsupported, disabled via the runtime
 * `notifications.push` app_setting, or permission was denied.
 *
 * `env.pushEnabled` (Firebase config presence) stays a hard deploy
 * prerequisite — checked lower down inside `requestPushToken`/`push.ts`, which
 * is a plain lib module with no access to the config query. The runtime
 * on/off switch lives here instead, at the one call site that has it.
 */
export function useEnablePush() {
  const userId = useSessionStore((s) => s.userId);
  const { flag } = useAppSettings();
  return useMutation({
    mutationFn: async (): Promise<boolean> => {
      if (!userId) return false;
      if (!flag('notifications.push')) return false;
      const token = await requestPushToken();
      if (!token) return false;
      await unwrap(notificationsRepository.registerToken(userId, token));
      return true;
    },
  });
}
